import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Statistic, Row, Col, Spin, message, InputNumber } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

interface RepeatData {
  line_user_id: string;
  display_name: string;
  first_coupon_use: string;
  repeat_coupon_use_count: number;
}

interface Summary {
  totalUsers: number;
  repeatUsers: number;
  repeatRate: number;
}

const RepeatAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RepeatData[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [days, setDays] = useState(30);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/repeat-rate', {
        params: { days }
      });

      if (response.data.success) {
        setData(response.data.data.details);
        setSummary(response.data.data.summary);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('リピート率分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const csvData = data.map(row => ({
      '会員名': row.display_name || '-',
      'LINE ID': row.line_user_id,
      '初回クーポン使用日': row.first_coupon_use,
      [`${days}日以内のリピート回数`]: row.repeat_coupon_use_count
    }));

    downloadCSV(csvData, `リピート率分析_${days}日以内.csv`);
    message.success('CSVファイルをダウンロードしました');
  };

  const columns = [
    {
      title: '会員名',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (text: string) => text || '-',
      ellipsis: true
    },
    {
      title: '初回クーポン使用日',
      dataIndex: 'first_coupon_use',
      key: 'first_coupon_use',
      width: 180
    },
    {
      title: `${days}日以内のリピート`,
      dataIndex: 'repeat_coupon_use_count',
      key: 'repeat_coupon_use_count',
      render: (count: number) => (
        <span className={`font-bold ${count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
          {count}回
        </span>
      ),
      align: 'right' as const,
      sorter: (a: RepeatData, b: RepeatData) => a.repeat_coupon_use_count - b.repeat_coupon_use_count,
      width: 150
    },
    {
      title: 'リピート状況',
      key: 'status',
      render: (_: any, record: RepeatData) => {
        if (record.repeat_coupon_use_count === 0) {
          return <span className="text-gray-400">未リピート</span>;
        } else if (record.repeat_coupon_use_count >= 3) {
          return <span className="text-green-600 font-bold">🔥 優良リピーター</span>;
        } else {
          return <span className="text-blue-600">✅ リピート済み</span>;
        }
      },
      width: 180
    }
  ];

  return (
    <div>
      {/* 設定カード */}
      <Card className="mb-4">
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              リピート期間（日数）
            </label>
            <InputNumber
              value={days}
              onChange={(value) => setDays(value || 30)}
              min={7}
              max={365}
              style={{ width: 120 }}
            />
          </div>

          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              更新
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={data.length === 0}
            >
              CSV出力
            </Button>
          </div>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">リピート率分析とは？</div>
            <div className="text-sm text-gray-600">
              クーポン使用後、指定期間内に再度クーポンを使用したかを分析します。
              クーポン施策がリピート購買に繋がっているかを評価できます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : summary ? (
        <>
          {/* サマリー統計 */}
          <Row gutter={16} className="mb-4">
            <Col span={8}>
              <Card>
                <Statistic
                  title="総ユーザー数"
                  value={summary.totalUsers}
                  suffix="名"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="リピートユーザー"
                  value={summary.repeatUsers}
                  suffix="名"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title={`リピート率（${days}日以内）`}
                  value={summary.repeatRate}
                  suffix="%"
                  precision={1}
                  valueStyle={{
                    color: summary.repeatRate >= 50 ? '#3f8600' :
                           summary.repeatRate >= 30 ? '#faad14' : '#cf1322'
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* データテーブル */}
          <Card title="ユーザー別リピート状況">
            <Table
              columns={columns}
              dataSource={data}
              rowKey="line_user_id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `全 ${total} 件`
              }}
              locale={{
                emptyText: '該当するデータがありません'
              }}
            />
          </Card>
        </>
      ) : (
        <Card>
          <div className="text-center text-gray-500 py-12">
            「更新」ボタンをクリックしてデータを読み込んでください
          </div>
        </Card>
      )}
    </div>
  );
};

export default RepeatAnalytics;
